// =============================================================================
// Citation → DOI resolver.
//
// Bridges the engine's citation slugs ("mei-2021", "addo-2025", hyphen-form)
// to the canonical reference bibliography in `content/references.json`
// (entries keyed by underscore form, "mei_2021"). Used by the pattern
// renderer to emit clickable citation anchors that link straight out to
// the publisher via DOI on first paint.
//
// This module is the single compile-time dependency between the engine
// bundle and the references bibliography. The runtime references.js
// loader is a separate concern (it powers the evidence-page auto-linker
// and the bibliography rendering on that page).
// =============================================================================

import refsJson from '../../content/references.json';

interface ReferenceEntry {
  topic: string;
  short: string;
  apa: string;
  doi_url: string;
  note?: string;
}

interface ReferencesFile {
  _meta: {
    description: string;
    last_updated: string;
    topic_order: string[];
    source?: string;
  };
  entries: Record<string, ReferenceEntry>;
}

const refs = refsJson as ReferencesFile;

/** Engine slugs hyphen-separated (mei-2021). JSON keys underscore (mei_2021). */
function slugToKey(slug: string): string {
  return slug.replace(/-/g, '_');
}

/** Escape text for safe embedding in an HTML attribute or body. */
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Acronym surnames that should render in all caps rather than title case.
 * Keyed by lowercase token as it appears in the slug; value is the
 * rendered form.
 */
const ACRONYM_AUTHORS: Record<string, string> = {
  easl: 'EASL',
  who: 'WHO',
  acog: 'ACOG',
  brinda: 'BRINDA',
};

/**
 * Derive a display label from a citation slug: "mei-2021" or "mei_2021"
 * both render as "Mei 2021"; "van-zeben-1990" or "van_zeben_1990" both
 * render as "Van Zeben 1990"; "easl-2022" or "easl_2022" render as
 * "EASL 2022". Both hyphen and underscore separators are accepted because
 * pattern-metadata.ts uses underscore form (matches references.json keys)
 * while older spec files used hyphen form.
 */
export function formatCitationLabel(slug: string): string {
  // Split on either separator so we don't render "mei_2021" as the literal
  // slug when the engine emits underscore-form citation keys.
  const parts = slug.split(/[-_]/);
  const last = parts[parts.length - 1];
  if (!last || !/^\d{4}$/.test(last)) return slug;
  const authorParts = parts.slice(0, -1);
  if (authorParts.length === 0) return slug;
  const author = authorParts
    .map((p) => {
      if (!p.length) return p;
      const ack = ACRONYM_AUTHORS[p.toLowerCase()];
      if (ack) return ack;
      return p[0]!.toUpperCase() + p.slice(1);
    })
    .join(' ');
  return `${author} ${last}`;
}

/**
 * Look up the reference entry for an engine slug. Returns undefined when
 * the slug has no bibliography entry — callers should fall back to a
 * plain-text label in that case.
 */
export function resolveReference(slug: string): ReferenceEntry | undefined {
  return refs.entries[slugToKey(slug)];
}

/**
 * Render a citation as an HTML anchor that opens the DOI in a new tab,
 * with a plain-text fallback when the slug has no entry in the bibliography.
 * Output is trusted HTML — callers MUST inject via innerHTML, not textContent.
 */
export function citationAnchorHtml(slug: string): string {
  const label = escapeHtml(formatCitationLabel(slug));
  const entry = resolveReference(slug);
  if (!entry || !entry.doi_url) return label;
  const href = escapeHtml(entry.doi_url);
  return `<a href="${href}" class="citation-link" target="_blank" rel="noopener noreferrer" data-citation-slug="${escapeHtml(slug)}">${label}</a>`;
}
